import { generateShadowInstApi } from '@/admin/services/http/generate-shadow-inst.api';
import type { ConfigureStoreOptions } from '@reduxjs/toolkit';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { applicationLogsApi } from '@/admin/services/http/application-logs.api';
import { companiesAutoCreationConfigsApi } from '@/admin/services/http/companies-auto-creation-configs.api';
import { companiesPlatformConfigsApi } from '@/admin/services/http/companies-platform-configs.api';
import { companiesApi } from '@/admin/services/http/companies.api';
import { companiesBrandingsApi } from '@/admin/services/http/company-branding.api';
import { officesApi } from '@/admin/services/http/offices.api';
import { retailDataProvidersApi } from '@/admin/services/http/retail-data-provider.api';
import { rolesApi } from '@/admin/services/http/roles.api';
import { usersApi } from '@/admin/services/http/users.api';
import authReducer from '../auth/store/reducers/auth.slice';
import { customerAdSetsApi } from '@/editor/services/http/ad-sets.api';
import { customerAdvertisementsApi } from '@/editor/services/http/advertisements.api';
import { customerBudgetsApi } from '@/editor/services/http/budgets.api';
import { customerCampaignsApi } from '@/editor/services/http/campaigns.api';
import { customerCreativesApi } from '@/editor/services/http/creatives.api';
import { mediaLibraryApi } from '@/editor/services/http/media-library.api';
import { customerMoovlyApi } from '@/editor/services/http/moovly.api';
import { customersOrdersApi } from '@/editor/services/http/orders.api';
import { customerPropertiesApi } from '@/editor/services/http/properties.api';
import { userRetailDataProviderApi } from '@/editor/services/http/retail-data-provider.api';
import { customerUsersApi } from '@/editor/services/http/users.api';
import creativeStepSliceReducer from '../editor/store/reducers/creative-step.slice';
import editorModalsDrawersReducer from '../editor/store/reducers/modals-drawers.slice';
import propertyInfoSliceReducer from '../editor/store/reducers/property-info.slice';
import editorActivePlatformsReducer from '../editor/store/reducers/selected-platforms.slice';
import adSetStepSliceReducer from '../editor/store/reducers/adSet-step.slice';
import { api } from '@/shared/services/api/api';
import { userProfileApi } from '@/shared/services/api/user-profiles.api';
import { rtkQueryErrorLogger } from '@/shared/services/middleware/RTQ.error-logger.middleware';
import { advertisementsAdminApi } from '@/admin/services/http/advertisements.api';
import { companyAdminsApi } from '@/admin/services/http/company-admins.api';

export const rootReducer = combineReducers({
  authReducer,
  editorActivePlatformsReducer,
  adSetStepSliceReducer,
  editorModalsDrawersReducer,
  creativeStepSliceReducer,
  propertyInfoSliceReducer,
  api: api.reducer,
  usersApi: usersApi.reducer,
  rolesApi: rolesApi.reducer,
  userProfileApi: userProfileApi.reducer,
  retailDataProvidersApi: retailDataProvidersApi.reducer,
  companiesApi: companiesApi.reducer,
  officesApi: officesApi.reducer,
  userRetailDataProviderApi: userRetailDataProviderApi.reducer,
  customerPropertiesApi: customerPropertiesApi.reducer,
  mediaLibraryApi: mediaLibraryApi.reducer,
  customerCampaignsApi: customerCampaignsApi.reducer,
  customerAdSetsApi: customerAdSetsApi.reducer,
  customerCreativesApi: customerCreativesApi.reducer,
  customerBudgetsApi: customerBudgetsApi.reducer,
  customersOrdersApi: customersOrdersApi.reducer,
  customerAdvertisementsApi: customerAdvertisementsApi.reducer,
  companiesPlatformConfigsApi: companiesPlatformConfigsApi.reducer,
  companiesAutoCreationConfigsApi: companiesAutoCreationConfigsApi.reducer,
  companiesBrandingsApi: companiesBrandingsApi.reducer,
  customerUsersApi: customerUsersApi.reducer,
  applicationLogsApi: applicationLogsApi.reducer,
  customerMoovlyApi: customerMoovlyApi.reducer,
  generateShadowInstApi: generateShadowInstApi.reducer,
  advertisementsAdminApi: advertisementsAdminApi.reducer,
  companyAdminsApi: companyAdminsApi.reducer,
});

export const createStore = (options?: ConfigureStoreOptions['preloadedState']) =>
  configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(api.middleware).concat(rtkQueryErrorLogger),
    ...options,
  });

export const store = createStore();

export type RootReducer = ReturnType<typeof rootReducer>;
export type RootState = ReturnType<typeof store.getState>;
export type AppStore = ReturnType<typeof createStore>;
export type AppDispatch = typeof store.dispatch;
